import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils/cn';

export interface SliderProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  showValue?: boolean;
  minLabel?: string;
  maxLabel?: string;
}

const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    { className, showValue = true, minLabel, maxLabel, value, ...props },
    ref
  ) => {
    return (
      <div className={cn('w-full', className)}>
        <div className="relative">
          <input
            type="range"
            ref={ref}
            value={value}
            className={cn(
              'w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              '[&::-webkit-slider-thumb]:appearance-none',
              '[&::-webkit-slider-thumb]:w-5',
              '[&::-webkit-slider-thumb]:h-5',
              '[&::-webkit-slider-thumb]:rounded-full',
              '[&::-webkit-slider-thumb]:bg-primary-600',
              '[&::-webkit-slider-thumb]:cursor-pointer',
              '[&::-webkit-slider-thumb]:border-2',
              '[&::-webkit-slider-thumb]:border-white',
              '[&::-webkit-slider-thumb]:shadow-md',
              '[&::-moz-range-thumb]:w-5',
              '[&::-moz-range-thumb]:h-5',
              '[&::-moz-range-thumb]:rounded-full',
              '[&::-moz-range-thumb]:bg-primary-600',
              '[&::-moz-range-thumb]:cursor-pointer',
              '[&::-moz-range-thumb]:border-2',
              '[&::-moz-range-thumb]:border-white'
            )}
            {...props}
          />
        </div>

        {(minLabel || maxLabel || showValue) && (
          <div className="flex justify-between items-center mt-2">
            {minLabel && (
              <span className="text-xs text-gray-500">{minLabel}</span>
            )}
            {showValue && value !== undefined && (
              <span className="text-sm font-medium text-primary-600">
                {value}
              </span>
            )}
            {maxLabel && (
              <span className="text-xs text-gray-500">{maxLabel}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Slider.displayName = 'Slider';

export { Slider };
